import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getUserData } from "../../../services/apiService";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { resolvePlatformSuperAdminStaffId } from "../../../utils/permissionUtils";
import {
  fetchFaqAiPlatformSettings,
  saveFaqAiPlatformSettings,
} from "../../../services/superAdminOrgService";
import "./SuperAdminFaqAiPlatformSettings.scss";

const EMPTY = {
  costInputPer1M: 0.4,
  costOutputPer1M: 1.6,
  dailyOrgRequestCap: 50,
  dailyUserRequestCap: 50,
  dailyCapMin: 1,
  dailyCapMax: 500,
  maxQuestionLength: 1000,
  maxQuestionLineBreaks: 6,
  retentionMonths: 18,
  azureBudgetAlertGbpMin: 20,
  azureBudgetAlertGbpMax: 50,
  environmentName: "",
  globalEnabled: false,
  azureEndpointSet: false,
  azureKeySet: false,
  azureDeployment: "",
  azureApiVersion: "",
  updatedUtc: "",
  updatedBy: "",
};

function toNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function Field({ id, label, hint, children }) {
  return (
    <div className="faq-ai-platform__field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <p>{hint}</p> : null}
    </div>
  );
}

function SuperAdminFaqAiPlatformSettings() {
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyRow = useCallback((row) => {
    const next = { ...EMPTY, ...row };
    setForm(next);
    setSaved(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const createdBy = resolvePlatformSuperAdminStaffId(getUserData());
      const row = await fetchFaqAiPlatformSettings({ createdBy });
      applyRow(row);
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Could not load FAQ AI platform settings.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  }, [applyRow, showAlert]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () =>
      [
        "costInputPer1M",
        "costOutputPer1M",
        "dailyOrgRequestCap",
        "dailyUserRequestCap",
        "dailyCapMin",
        "dailyCapMax",
        "maxQuestionLength",
        "maxQuestionLineBreaks",
        "retentionMonths",
        "azureBudgetAlertGbpMin",
        "azureBudgetAlertGbpMax",
      ].some((key) => Number(form[key]) !== Number(saved[key])),
    [form, saved],
  );

  const setNumber = (key) => (event) => {
    setForm((prev) => ({
      ...prev,
      [key]: toNumber(event.target.value, prev[key]),
    }));
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const createdBy = resolvePlatformSuperAdminStaffId(getUserData());
      const row = await saveFaqAiPlatformSettings({
        createdBy,
        ...form,
      });
      applyRow(row);
      showAlert({
        title: "Saved",
        message:
          row.message ||
          "FAQ AI platform settings saved. They apply on the next ask — no deploy needed.",
        type: "success",
      });
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Could not save FAQ AI platform settings.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="faq-ai-platform">
      <button
        type="button"
        className="faq-ai-platform__toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>
          FAQ AI platform settings
          <em>Database — change without a code deploy</em>
        </span>
        <i className={`fas fa-chevron-${open ? "up" : "down"}`} aria-hidden="true" />
      </button>

      {open ? (
        <div className="faq-ai-platform__body">
          {loading ? (
            <p className="faq-ai-platform__muted">Loading platform settings…</p>
          ) : (
            <>
              <p className="faq-ai-platform__lead">
                Caps, cost rates, question limits and retention live in this
                environment database. Azure endpoint, API key, deployment and
                the global On/Off switch stay in Web.config — those need an
                API restart after a file change.
              </p>

              <div className="faq-ai-platform__meta">
                <div>
                  <span>Environment</span>
                  <strong>{form.environmentName || "—"}</strong>
                </div>
                <div>
                  <span>Global kill switch</span>
                  <strong>{form.globalEnabled ? "On" : "Off"}</strong>
                </div>
                <div>
                  <span>Azure key</span>
                  <strong>{form.azureKeySet ? "Set" : "Missing"}</strong>
                </div>
                <div>
                  <span>Deployment</span>
                  <strong>{form.azureDeployment || "—"}</strong>
                </div>
              </div>

              <div className="faq-ai-platform__grid">
                <Field
                  id="faq-ai-cost-in"
                  label="Input cost £ / 1M tokens"
                  hint="Used only to estimate cost on the usage log."
                >
                  <input
                    id="faq-ai-cost-in"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.costInputPer1M}
                    disabled={saving}
                    onChange={setNumber("costInputPer1M")}
                  />
                </Field>
                <Field
                  id="faq-ai-cost-out"
                  label="Output cost £ / 1M tokens"
                  hint="Used only to estimate cost on the usage log."
                >
                  <input
                    id="faq-ai-cost-out"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.costOutputPer1M}
                    disabled={saving}
                    onChange={setNumber("costOutputPer1M")}
                  />
                </Field>
                <Field
                  id="faq-ai-org-cap"
                  label="Default org asks / UTC day"
                  hint="New organisations start from this default."
                >
                  <input
                    id="faq-ai-org-cap"
                    type="number"
                    min={form.dailyCapMin}
                    max={form.dailyCapMax}
                    value={form.dailyOrgRequestCap}
                    disabled={saving}
                    onChange={setNumber("dailyOrgRequestCap")}
                  />
                </Field>
                <Field
                  id="faq-ai-user-cap"
                  label="Default user asks / UTC day"
                  hint="Per-user default for organisations that have not set their own cap."
                >
                  <input
                    id="faq-ai-user-cap"
                    type="number"
                    min={form.dailyCapMin}
                    max={form.dailyCapMax}
                    value={form.dailyUserRequestCap}
                    disabled={saving}
                    onChange={setNumber("dailyUserRequestCap")}
                  />
                </Field>
                <Field
                  id="faq-ai-cap-min"
                  label="Daily cap minimum"
                  hint="Lowest value Super Admin can set on an organisation."
                >
                  <input
                    id="faq-ai-cap-min"
                    type="number"
                    min="1"
                    max="9999"
                    value={form.dailyCapMin}
                    disabled={saving}
                    onChange={setNumber("dailyCapMin")}
                  />
                </Field>
                <Field
                  id="faq-ai-cap-max"
                  label="Daily cap maximum"
                  hint="Highest value Super Admin can set on an organisation."
                >
                  <input
                    id="faq-ai-cap-max"
                    type="number"
                    min="1"
                    max="9999"
                    value={form.dailyCapMax}
                    disabled={saving}
                    onChange={setNumber("dailyCapMax")}
                  />
                </Field>
                <Field
                  id="faq-ai-q-len"
                  label="Maximum question length"
                  hint="Characters. Allowed range 50–4000."
                >
                  <input
                    id="faq-ai-q-len"
                    type="number"
                    min="50"
                    max="4000"
                    value={form.maxQuestionLength}
                    disabled={saving}
                    onChange={setNumber("maxQuestionLength")}
                  />
                </Field>
                <Field
                  id="faq-ai-q-breaks"
                  label="Maximum line breaks"
                  hint="Rejects long pasted blocks. Allowed range 1–40."
                >
                  <input
                    id="faq-ai-q-breaks"
                    type="number"
                    min="1"
                    max="40"
                    value={form.maxQuestionLineBreaks}
                    disabled={saving}
                    onChange={setNumber("maxQuestionLineBreaks")}
                  />
                </Field>
                <Field
                  id="faq-ai-retain"
                  label="Log retention (months)"
                  hint="Allowed range 12–24."
                >
                  <input
                    id="faq-ai-retain"
                    type="number"
                    min="12"
                    max="24"
                    value={form.retentionMonths}
                    disabled={saving}
                    onChange={setNumber("retentionMonths")}
                  />
                </Field>
                <Field
                  id="faq-ai-budget-min"
                  label="Azure budget alert £ min"
                  hint="Reminder only — set the actual alert in Azure Portal."
                >
                  <input
                    id="faq-ai-budget-min"
                    type="number"
                    min="1"
                    value={form.azureBudgetAlertGbpMin}
                    disabled={saving}
                    onChange={setNumber("azureBudgetAlertGbpMin")}
                  />
                </Field>
                <Field
                  id="faq-ai-budget-max"
                  label="Azure budget alert £ max"
                  hint="Reminder only — set the actual alert in Azure Portal."
                >
                  <input
                    id="faq-ai-budget-max"
                    type="number"
                    min="1"
                    value={form.azureBudgetAlertGbpMax}
                    disabled={saving}
                    onChange={setNumber("azureBudgetAlertGbpMax")}
                  />
                </Field>
              </div>

              <div className="faq-ai-platform__actions">
                <p className="faq-ai-platform__muted">
                  Last updated{" "}
                  {form.updatedUtc
                    ? new Date(form.updatedUtc).toISOString().replace("T", " ").slice(0, 19)
                    : "—"}{" "}
                  UTC
                  {form.updatedBy ? ` by ${form.updatedBy}` : ""}.
                  API version {form.azureApiVersion || "—"}
                  {form.azureEndpointSet ? " · endpoint set" : " · endpoint missing"}.
                </p>
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={saving || !dirty}
                    onClick={() => setForm(saved)}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={saving || !dirty}
                    onClick={save}
                  >
                    {saving ? "Saving…" : "Save platform settings"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}

      <InfoAlert
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        icon={alertState.icon}
        buttonText={alertState.buttonText}
        cancelText={alertState.cancelText}
        onConfirm={() => {
          if (alertState.onConfirm) alertState.onConfirm();
          hideAlert();
        }}
        onCancel={() => {
          if (alertState.onCancel) alertState.onCancel();
          hideAlert();
        }}
      />
    </section>
  );
}

export default SuperAdminFaqAiPlatformSettings;
